import { LoanDefinitionTable } from '@dealstack/frontend/shared/components/LoanDefinitonTable/LoanDefinitionTable';
import {
	getLoanDefinitionValidationSchema,
	LoanDefinitionForm,
	LoanDefinitionFormValues,
} from '@dealstack/frontend/shared/components/LoanDefinitonTable/useLoanDefinitionTableController';
import { usePermissionRequest } from '@dealstack/frontend/shared/components/PermissionWrapper/PermissionWrapper.tsx';
import { SectionHeader } from '@dealstack/frontend/shared/components/SectionHeader/SectionHeader';
import { SecuritiesTable } from '@dealstack/frontend/shared/components/SecuritiesTable/SecuritiesTable';
import {
	getSecuritiesValidationSchema,
	SecuritiesFormValues,
} from '@dealstack/frontend/shared/components/SecuritiesTable/useSecuritiesTableController';
import { SecurityDeleteConfirmationDrawer } from '@dealstack/frontend/shared/components/SecurityDeleteConfirmationDrawer/SecurityDeleteConfirmationDrawer.tsx';
import { SecurityDrawer } from '@dealstack/frontend/shared/components/SecurityDrawer/SecurityDrawer';
import { SecurityFormData } from '@dealstack/frontend/shared/components/SecurityDrawer/SecurityTypes';
import { TabsButtons as Tabs } from '@dealstack/frontend/shared/components/Tabs/Tabs.v3';
import { useTabs } from '@dealstack/frontend/shared/components/Tabs/useTabs.v3';
import { VestingTerms } from '@dealstack/frontend/shared/components/VestingTerms/VestingTerms';
import { isStandardBackendError } from '@dealstack/frontend/shared/dataExchange/ApiLayer/apiLayer';
import {
	createMIPLoanDefinition,
	deleteMIPLoanDefinitionData,
	updateMIPLoanDefinitionData,
} from '@dealstack/frontend/shared/dataExchange/services/mip.service';
import {
	bulkCreateSecurities,
	bulkUpdateSecurities,
	getSecuritiesDataForMip,
	softDeleteSecurity,
} from '@dealstack/frontend/shared/dataExchange/services/securities.ts';
import {
	useTranslationCommonUi,
	useTranslationDefault,
	useTranslationMipCreate,
} from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks';
import { showToastV2 } from '@dealstack/frontend/shared/mechanism/toast/useToastV2';
import {
	PermissionRole,
	ResourceType,
} from '@dealstack/shared/lib/common/fineGrainedAuthorization/permissions.ts';
import { formatUnicorn } from '@dealstack/shared/lib/format/formatUnicorn.ts';
import { defaultToEmptyString } from '@dealstack/shared/lib/format/string.ts';
import {
	BulkCreateSecuritiesDto,
	BulkUpdateSecuritiesDto,
	LoanDefinitionDto,
	SecurityIssuedIn,
	SecurityParticipationThresholdBasis,
	SecurityType,
} from '@dealstack/shared/lib/generated';
import API_PATHS from '@dealstack/shared/lib/generated/apiPaths.ts';
import { yupResolver } from '@hookform/resolvers/yup';
import { Box } from '@mantine/core';
import { useMutation, useQuery } from '@tanstack/react-query';
import classNames from 'classnames';
import { isEmpty, isEqual, omit } from 'lodash';
import { useContext, useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import {
	FormSingleSecurity,
	SecuritiesForm,
} from '@dealstack/frontend/shared/components/SecuritiesTable/useSecuritiesTableController.types';
import { MIPCreateContext } from '@dealstack/frontend/app/pages/Project/MIP/MIPCreate/MIPCreateContext';
import { MIP_CREATE_ROUTES } from '@dealstack/frontend/app/pages/Project/MIP/MIPCreate/useMIPCreateController';
import {
	SAVE_FAILED,
	SAVE_INVALID,
	SAVE_OK,
	useWizardStep,
} from '@dealstack/frontend/shared/components/Wizard/WizardStepRegistry';
import type { WizardSaveResult } from '@dealstack/frontend/shared/components/Wizard/WizardStepRegistry.types';
import { nullToUndefined } from './Securities.utils';

interface BulkCreateSecurityResponse {
	created: number;
	updated: number;
	items: {
		id: string;
		[key: string]: unknown;
	}[];
}

const isBulkCreateSecurityResponse = (value: unknown): value is BulkCreateSecurityResponse =>
	typeof value === 'object' &&
	value !== null &&
	'created' in value &&
	'items' in value &&
	Array.isArray(value.items);

const createFormSingleSecurity = ({
	id,
	name,
	type,
	votesPerUnit,
	nominalValue,
	isSubjectToVesting,
	fairMarketValuePerUnit,
	hasTransactions,
	locked,
}: {
	id: string;
	name: string;
	type: SecurityType;
	votesPerUnit: number | null | undefined;
	nominalValue: number | null | undefined;
	isSubjectToVesting: boolean;
	fairMarketValuePerUnit?: number | null;
	hasTransactions?: boolean;
	locked?: boolean;
}): FormSingleSecurity => {
	return {
		id,
		localId: id,
		name,
		type,
		votesPerUnit: votesPerUnit === null ? undefined : votesPerUnit,
		nominalValue: nominalValue === null ? undefined : nominalValue,
		isSubjectToVesting,
		fairMarketValuePerUnit:
			fairMarketValuePerUnit === null ? undefined : fairMarketValuePerUnit,
		hasTransactions: hasTransactions || false,
		locked: locked || false,
		isOnEdit: false,
		isOnAdd: false,
		residueParticipationThresholdBasis:
			SecurityParticipationThresholdBasis.TOTAL_SOURCES_FOR_ISSUER,
	};
};

export const Securities = () => {
	const tMipCreate = useTranslationMipCreate();
	const mipCreateController = useContext(MIPCreateContext);
	const tCommonUi = useTranslationCommonUi();
	const tDefault = useTranslationDefault();

	const securitiesForm = useForm<SecuritiesForm>({
		defaultValues: {
			[SecuritiesFormValues.securities]: [],
		},
		mode: 'all',
		resolver: yupResolver(getSecuritiesValidationSchema(tDefault)),
	});

	const loanDefinitionForm = useForm<LoanDefinitionForm>({
		defaultValues: {
			[LoanDefinitionFormValues.loanDefinitions]: [],
		},
		mode: 'all',
		resolver: yupResolver(getLoanDefinitionValidationSchema(tDefault)),
	});

	const [isDrawerOpen, setIsDrawerOpen] = useState(false);
	const [securityToEdit, setSecurityToEdit] = useState<FormSingleSecurity | null>(null);
	const [confirmDeleteOpened, setConfirmDeleteOpened] = useState(false);
	const [securityToDelete, setSecurityToDelete] = useState<{ id: string; name: string } | null>(
		null
	);

	const drawerForm = useForm<SecurityFormData>({
		defaultValues: {
			name: '',
			type: SecurityType.ORDINARY_SHARE,
			votesPerUnit: 1,
			nominalValue: 0,
			isSubjectToVesting: false,
		},
	});

	const { hasPermission: hasAdminPermissions } = usePermissionRequest({
		resourceType: ResourceType.DEAL,
		resourceId: defaultToEmptyString(mipCreateController.dealId),
		permissionRole: PermissionRole.ADMIN,
	});

	const bulkCreateSecuritiesMutation = useMutation({
		mutationFn: (args: BulkCreateSecuritiesDto) => bulkCreateSecurities(args),
	});
	const bulkUpdateSecuritiesMutation = useMutation({
		mutationFn: (args: BulkUpdateSecuritiesDto) => bulkUpdateSecurities(args),
	});
	const deleteSecurityMutation = useMutation({
		mutationFn: softDeleteSecurity,
	});
	const createLoanDefinitionMutation = useMutation({
		mutationFn: (args: { loansDefinitions: LoanDefinitionDto[] }) =>
			createMIPLoanDefinition(args),
	});
	const updateLoanDefinitionMutation = useMutation({
		mutationFn: (args: { body: LoanDefinitionDto; loanDefinitionId: string }) =>
			updateMIPLoanDefinitionData(args),
	});
	const deleteLoanDefinitionMutation = useMutation({
		mutationFn: (args: { loanDefinitionId: string }) => deleteMIPLoanDefinitionData(args),
	});

	const mipData = mipCreateController?.getMipRequest?.response?.payload;

	const getSecuritiesDataForMipRequest = useQuery({
		queryKey: [
			formatUnicorn(API_PATHS.SECURITY_MIP_SECURITIES_MIPID_GET, {
				mipId: mipData?.id as string,
			}),
		],
		queryFn: ({ signal }) =>
			getSecuritiesDataForMip(
				formatUnicorn(API_PATHS.SECURITY_MIP_SECURITIES_MIPID_GET, {
					mipId: mipData?.id as string,
				}),
				undefined,
				signal
			),
		enabled: Boolean(mipData?.id && mipData?.entityId),
		meta: { errorToast: tDefault('common.toast.error.message') },
	});

	useEffect(() => {
		const securityRows: FormSingleSecurity[] =
			getSecuritiesDataForMipRequest.data?.map((security) => ({
				id: security.id,
				localId: security.id,
				name: security.name,
				type: security.type,
				votesPerUnit: security.votesPerUnit,
				nominalValue: security.nominalValue,
				fairMarketValuePerUnit: security.fairMarketValuePerUnit,
				hasTransactions: security.hasTransactions,
				isSubjectToVesting: security.isSubjectToVesting,
				locked: false,
				isOnEdit: false,
				isOnAdd: false,
				residueRights: false,
				issuedIn: security.issuedIn,
				residueParticipationThresholdBasis:
					SecurityParticipationThresholdBasis.TOTAL_SOURCES_FOR_ISSUER,
			})) ?? [];

		securitiesForm.reset({
			[SecuritiesFormValues.securities]: securityRows,
		});
	}, [getSecuritiesDataForMipRequest.data]);

	useEffect(() => {
		const loanDefinitionRows = mipData?.loanDefinitions.map((item) => ({
			...item,
			localId: item.id,
			isOnEdit: false,
		}));
		loanDefinitionForm.reset({
			[LoanDefinitionFormValues.loanDefinitions]: loanDefinitionRows ?? [],
		});
	}, [mipData?.loanDefinitions]);

	const handleEditSecurity = (security: FormSingleSecurity) => {
		setSecurityToEdit(security);
		drawerForm.reset({
			id: security.id,
			name: security.name,
			type: security.type,
			votesPerUnit: security.votesPerUnit,
			nominalValue: security.nominalValue,
			isSubjectToVesting: security.isSubjectToVesting,
			issuedIn: security.issuedIn || SecurityIssuedIn.UNITS,
		});
		setIsDrawerOpen(true);
	};

	const handleAddSecurity = () => {
		setSecurityToEdit(null);
		drawerForm.reset({
			name: '',
			type: SecurityType.ORDINARY_SHARE,
			votesPerUnit: 1,
			nominalValue: 0,
			isSubjectToVesting: false,
		});
		setIsDrawerOpen(true);
	};

	const handleCloseDrawer = () => {
		setIsDrawerOpen(false);
		// The row's edit button flags it isOnEdit, which disables Continue until cleared.
		const securities = securitiesForm.getValues(SecuritiesFormValues.securities) ?? [];
		if (securities.some((security) => security.isOnEdit)) {
			securitiesForm.setValue(
				SecuritiesFormValues.securities,
				securities.map((security) => ({ ...security, isOnEdit: false }))
			);
		}
	};

	const handleSaveSecurity = async (data: SecurityFormData) => {
		if (!mipData?.id) {
			showToastV2({
				title: tDefault('mip-create.common.toast.error.message'),
				state: 'error',
			});
			return;
		}

		const entityId = mipData.entityId || mipData.entity?.id;

		if (!entityId) {
			showToastV2({
				title: tDefault('mip-create.common.toast.error.message'),
				state: 'error',
			});
			return;
		}

		if (securityToEdit?.id) {
			try {
				await bulkUpdateSecuritiesMutation.mutateAsync({
					securities: [
						{
							id: securityToEdit.id,
							name: data.name,
							type: data.type,
							votesPerUnit: nullToUndefined(data.votesPerUnit),
							nominalValue: nullToUndefined(data.nominalValue),
							fairMarketValuePerUnit: nullToUndefined(data.fairMarketValuePerUnit),
							issuedIn: data.issuedIn,
							securityProgrammeSettings: {
								mipId: mipData.id,
								isSubjectToVesting: data.isSubjectToVesting,
							},
						},
					],
				});
			} catch (error) {
				showToastV2({
					title: tDefault('mip-create.common.toast.error.message'),
					state: 'error',
				});
				throw error;
			}

			showToastV2({
				title: tDefault('mip-create.common.toast.success.message'),
				state: 'success',
			});

			const securities = securitiesForm.getValues(SecuritiesFormValues.securities) || [];

			const securityIndex = securities.findIndex((s) => s.id === securityToEdit.id);

			if (securityIndex >= 0) {
				const updatedSecurity = createFormSingleSecurity({
					id: securities[securityIndex].id as string,
					name: data.name,
					type: data.type,
					votesPerUnit: data.votesPerUnit,
					nominalValue: data.nominalValue,
					isSubjectToVesting: data.isSubjectToVesting,
					fairMarketValuePerUnit: data.fairMarketValuePerUnit,
					hasTransactions: securities[securityIndex].hasTransactions,
					locked: securities[securityIndex].locked,
				});

				const updatedSecurities = [...securities];
				updatedSecurities[securityIndex] = updatedSecurity;

				securitiesForm.setValue(SecuritiesFormValues.securities, updatedSecurities);

				await securitiesForm.trigger(SecuritiesFormValues.securities);
			}

			handleCloseDrawer();
			await getSecuritiesDataForMipRequest.refetch();
		} else {
			let response: unknown;
			try {
				response = await bulkCreateSecuritiesMutation.mutateAsync({
					issuerId: entityId,
					securities: [
						{
							name: data.name,
							type: data.type,
							currency: mipData.currency,
							votesPerUnit: nullToUndefined(data.votesPerUnit),
							nominalValue: nullToUndefined(data.nominalValue),
							fairMarketValuePerUnit: nullToUndefined(data.fairMarketValuePerUnit),
							securityProgrammeSettings: {
								mipId: mipData.id,
								isSubjectToVesting: data.isSubjectToVesting,
							},
						},
					],
				});
			} catch (error) {
				showToastV2({
					title: tDefault('mip-create.common.toast.error.message'),
					state: 'error',
				});
				throw error;
			}

			showToastV2({
				title: tDefault('mip-create.common.toast.success.message'),
				state: 'success',
			});

			if (
				isBulkCreateSecurityResponse(response) &&
				response.created > 0 &&
				response.items.length > 0
			) {
				const createdSecurity = response.items[0];

				const newSecurity = createFormSingleSecurity({
					id: createdSecurity.id,
					name: data.name,
					type: data.type,
					votesPerUnit: data.votesPerUnit,
					nominalValue: data.nominalValue,
					isSubjectToVesting: data.isSubjectToVesting,
					fairMarketValuePerUnit: data.fairMarketValuePerUnit,
				});

				const securities = securitiesForm.getValues(SecuritiesFormValues.securities) || [];

				securitiesForm.setValue(SecuritiesFormValues.securities, [
					...securities,
					newSecurity,
				]);

				await securitiesForm.trigger(SecuritiesFormValues.securities);
			}

			handleCloseDrawer();
			await getSecuritiesDataForMipRequest.refetch();
		}
	};

	const handleRemoveSecurity = (securityId: string) => {
		const securities = securitiesForm.getValues(SecuritiesFormValues.securities) || [];
		const security = securities.find((s) => s.id === securityId);
		setSecurityToDelete({ id: securityId, name: security?.name || '' });
		setIsDrawerOpen(false);
		setConfirmDeleteOpened(true);
	};

	const handleConfirmDelete = async () => {
		if (!securityToDelete?.id || !mipData?.id) {
			showToastV2({
				title: tDefault('mip-create.common.toast.error.message'),
				state: 'error',
			});
			return;
		}

		await deleteSecurityMutation.mutateAsync(securityToDelete.id, {
			onSuccess: () => {
				showToastV2({
					title: tDefault('mip-create.common.toast.success.message'),
					state: 'success',
				});

				const securities = securitiesForm.getValues(SecuritiesFormValues.securities) || [];
				const updatedSecurities = securities.filter((s) => s.id !== securityToDelete.id);
				securitiesForm.setValue(SecuritiesFormValues.securities, updatedSecurities);
			},
			onError: (responseError: unknown) => {
				if ((responseError as { error?: { code?: number } })?.error?.code === 11010101) {
					showToastV2({
						title: tDefault('mip-create.common.toast.error.has_transactions'),
						state: 'error',
					});
				} else {
					showToastV2({
						title: tDefault('mip-create.common.toast.error.message'),
						state: 'error',
					});
				}
			},
		});

		await getSecuritiesDataForMipRequest.refetch();
	};

	const onSubmit = async (): Promise<WizardSaveResult> => {
		if (!mipData) {
			return SAVE_INVALID;
		}
		let result: WizardSaveResult = SAVE_OK;

		mipCreateController?.setIsLoading(true);
		let isAnyFailed = false;
		let isErrorToastShown = false;

		const formLoanDefinitions = loanDefinitionForm.getValues(
			LoanDefinitionFormValues.loanDefinitions
		);
		const loanDefinitionsToCreate = formLoanDefinitions.filter((row) => !row.id);
		const loanDefinitionsToUpdate = formLoanDefinitions.filter((loanDefinition) => {
			if (!mipData?.loanDefinitions) {
				return false;
			}
			const existingLoanDefinitionIndex = mipData.loanDefinitions.findIndex(
				(existingLoanDefinition) => existingLoanDefinition.id === loanDefinition.id
			);
			if (existingLoanDefinitionIndex < 0) {
				return false;
			}
			const existingLoanDefinition = mipData?.loanDefinitions[existingLoanDefinitionIndex];
			return !isEqual(existingLoanDefinition, omit(loanDefinition, ['localId', 'isOnEdit']));
		});
		const loanDefinitionsToDelete = mipData?.loanDefinitions.filter(
			(item) =>
				!formLoanDefinitions.some((formLoanDefinition) => formLoanDefinition.id === item.id)
		);

		const createLoansDefinitionsPromise = (async () => {
			if (!loanDefinitionsToCreate?.length) {
				return;
			}
			try {
				await createLoanDefinitionMutation.mutateAsync({
					loansDefinitions: loanDefinitionsToCreate.map((loan) => ({
						type: loan.type,
						name: loan.name,
						funderPersonId: loan.funderIsNew ? undefined : loan.funderId,
						funderPersonNameToCreate: loan.funderIsNew ? loan.funderName : undefined,
						mipId: mipData.id,
					})),
				});
			} catch {
				isAnyFailed = true;
			}
		})();

		const updateLoansDefinitionsPromise = (() => {
			if (!loanDefinitionsToUpdate?.length) {
				return;
			}
			return loanDefinitionsToUpdate.map(async (loanDefinition) => {
				if (!loanDefinition?.id) {
					return;
				}

				try {
					await updateLoanDefinitionMutation.mutateAsync({
						loanDefinitionId: loanDefinition.id,
						body: {
							type: loanDefinition.type,
							name: loanDefinition.name,
							funderPersonId: loanDefinition.funderIsNew
								? undefined
								: loanDefinition.funderId,
							funderPersonNameToCreate: loanDefinition.funderIsNew
								? loanDefinition.funderName
								: undefined,
							mipId: mipData.id,
						},
					});
				} catch {
					isAnyFailed = true;
				}
			});
		})();

		const deleteLoanDefinitionsPromise = (() => {
			if (!loanDefinitionsToDelete?.length) {
				return;
			}
			return loanDefinitionsToDelete.map(async (loanDefinition) => {
				if (!loanDefinition.id) {
					return;
				}

				try {
					await deleteLoanDefinitionMutation.mutateAsync({
						loanDefinitionId: loanDefinition.id,
					});
				} catch (responseError) {
					if (isStandardBackendError(responseError)) {
						isErrorToastShown = true;
						showToastV2({
							title: tDefault(`error_codes.${responseError.error?.code}`),
							state: 'error',
						});
					}
					isAnyFailed = true;
				}
			});
		})();

		await Promise.allSettled([
			createLoansDefinitionsPromise,
			updateLoansDefinitionsPromise,
			deleteLoanDefinitionsPromise,
		]).then(() => {
			if (isAnyFailed) {
				mipCreateController?.updateBackupData(MIP_CREATE_ROUTES.GENERAL, {
					securities: securitiesForm.getValues(SecuritiesFormValues.securities),
					loanDefinitions: loanDefinitionForm.getValues(
						LoanDefinitionFormValues.loanDefinitions
					),
				});
				if (!isErrorToastShown) {
					showToastV2({
						title: tDefault('mip-create.common.toast.error.message'),
						state: 'error',
					});
				}
				result = SAVE_FAILED;
			} else {
				mipCreateController?.removeBackupData(MIP_CREATE_ROUTES.GENERAL);
			}
		});

		await Promise.all([
			mipCreateController?.getMipRequest?.fetch().concludeWithPromise(),
			getSecuritiesDataForMipRequest.refetch(),
		]);
		mipCreateController?.setIsLoading(false);
		return result;
	};

	useEffect(() => {
		const securitiesFields = securitiesForm.getValues(SecuritiesFormValues.securities);
		const securitiesFieldsOnEditionMode = securitiesFields.filter((field) => field.isOnEdit);
		const loanDefinitionFields = loanDefinitionForm.getValues(
			LoanDefinitionFormValues.loanDefinitions
		);
		const loanDefinitionFieldsOnEditionMode = loanDefinitionFields.filter(
			(field) => field.isOnEdit
		);
		const isSecuritiesOnEdit = securitiesFieldsOnEditionMode.length > 0;
		const isLoansOnEdit = loanDefinitionFieldsOnEditionMode.length > 0;
		const shouldDisableContinue = isSecuritiesOnEdit || isLoansOnEdit;

		mipCreateController?.setDisableContinueButton(shouldDisableContinue);
		if (shouldDisableContinue) {
			mipCreateController?.setPopoverMessage(tCommonUi('securities.continue_disabled_info'));
		}
	}, [
		securitiesForm.watch(SecuritiesFormValues.securities),
		securitiesForm.formState,
		loanDefinitionForm.watch(LoanDefinitionFormValues.loanDefinitions),
		loanDefinitionForm.formState,
	]);

	// Unmount only: a row left in edit mode must not keep Continue locked on the next step.
	useEffect(
		() => () => {
			mipCreateController?.setDisableContinueButton(false);
		},
		[]
	);

	useWizardStep(onSubmit);

	const securityTab = {
		id: 'securities',
		name: tMipCreate('securities.security_tab_title'),
	};

	const loansTab = {
		id: 'loans',
		name: tMipCreate('securities.loans_tab_title'),
	};

	const tabList = [securityTab, loansTab];

	const { tabs, selectedTabId, onSelectedTabIdChange } = useTabs({ tabs: tabList });

	const securities = securitiesForm.watch(SecuritiesFormValues.securities);

	const anySecurityHasSubjectToVesting = (
		!isEmpty(securities) ? securities : getSecuritiesDataForMipRequest?.data
	)?.some((security) => security.isSubjectToVesting);

	const getSecurityFormData = () => {
		if (!securityToEdit) {
			return {
				type: SecurityType.ORDINARY_SHARE,
				name: '',
				votesPerUnit: 1,
				nominalValue: 0,
				isSubjectToVesting: false,
				issuedIn: SecurityIssuedIn.UNITS,
			};
		}

		const securityData: SecurityFormData = {
			id: securityToEdit.id,
			name: securityToEdit.name,
			type: securityToEdit.type,
			votesPerUnit: securityToEdit.votesPerUnit,
			nominalValue: securityToEdit.nominalValue,
			isSubjectToVesting: securityToEdit.isSubjectToVesting,
			fairMarketValuePerUnit: securityToEdit.fairMarketValuePerUnit,
			issuedIn: securityToEdit.issuedIn,
		};

		return securityData;
	};

	return (
		<>
			<Tabs
				tabs={tabs}
				selectedTabId={selectedTabId}
				onSelectedTabIdChange={onSelectedTabIdChange}
			/>

			{selectedTabId === securityTab.id && (
				<>
					<div className="mt-8">
						<SectionHeader title={securityTab.name} />
					</div>

					<SecuritiesTable
						securitiesForm={securitiesForm}
						onEditSecurity={handleEditSecurity}
						onAddSecurity={handleAddSecurity}
					/>

					<Box
						className={classNames({
							'opacity-50': !anySecurityHasSubjectToVesting,
							'pointer-events-none': !anySecurityHasSubjectToVesting,
						})}
						pt="0.5rem"
					>
						<div className="my-4 mb-20">
							<VestingTerms skipDeleteConfirmation />
						</div>
					</Box>
				</>
			)}

			{selectedTabId === loansTab.id && (
				<>
					<div className="mt-8">
						<SectionHeader title={loansTab.name} />
					</div>

					<LoanDefinitionTable loanDefinitionForm={loanDefinitionForm} />
				</>
			)}

			<SecurityDrawer
				opened={isDrawerOpen}
				onClose={handleCloseDrawer}
				securityData={getSecurityFormData()}
				onSave={handleSaveSecurity}
				onRemove={handleRemoveSecurity}
				context="mipCreate"
				hasAdminPermissions={hasAdminPermissions}
			/>

			<SecurityDeleteConfirmationDrawer
				opened={confirmDeleteOpened}
				onClose={() => setConfirmDeleteOpened(false)}
				securityName={securityToDelete?.name}
				isSuccess={deleteSecurityMutation.isSuccess}
				isLoading={deleteSecurityMutation.isPending}
				onConfirm={handleConfirmDelete}
			/>
		</>
	);
};
